import React, { useState } from 'react';
import './TermsModal.css';

const CURRENT_TERMS_VERSION = '2025-11-02';

const TERMS_CONTENT = `
# PartyDecider · Terms & Conditions

**Last updated: November 2, 2025**

Welcome to PartyDecider (the "Service") operated by FLW Connect Technologies ("we", "us", "our"). By accessing or using PartyDecider you agree to these Terms & Conditions ("Terms"). If you do not agree, do not use the Service.

## 1. Who can use the Service

You must be at least 13 years old to use PartyDecider. If you are under 18 you must have parental consent.

You agree to provide accurate information when creating an account and to keep credentials secure. Accounts created by fraudulent means may be suspended or removed.

## 2. Accounts, authentication & linking providers

PartyDecider supports sign-in providers (Google, Microsoft, Facebook, email link, phone OTP). Authentication is handled by Firebase or other providers.

We store basic auth profile data (UID, email, display name, provider). You must not impersonate others.

If you sign in with multiple providers, accounts are treated as separate unless you explicitly link them. You are responsible for linking accounts if you want a unified profile.

## 3. User content & conduct

"User Content" = any text, images, files, event details, polls, raffles, messages, or other content uploaded, created, or posted by users.

You retain ownership of your User Content, but by posting you grant us a worldwide, royalty-free license to host, display, distribute, and modify it as necessary to operate the Service.

**Prohibited content and activities (examples, not exhaustive):**

- Illegal activity or instructions to commit illegal acts.
- Pornography, sexual content involving minors, or sexual exploitation.
- Hate speech, harassment, threats, or targeted abuse.
- Fraud, phishing, impersonation, or identity theft.
- Intellectual property infringement (unauthorized use of copyrighted material).
- Malware, hacking, network disruption, DDoS, or attempts to access unauthorized systems.
- Spam, bulk unsolicited messaging, or bypassing rate limits.
- Facilitating or promoting criminal marketplaces, weapons, controlled substances, or regulated goods.
- Evading bans, creating sockpuppet accounts, or coordinating abuse.

We reserve the right (but have no obligation) to remove or block User Content that violates these Terms or that we deem harmful, and to suspend or terminate offending accounts.

## 4. Moderation, reporting & takedown

If you see content that violates these Terms, report it using our in-app reporting or email support@partydecider.com.

We may remove content, disable accounts, or retain copies of content for investigations and law enforcement requests.

We may cooperate with law enforcement and disclose account details when required by law or to prevent imminent harm.

## 5. AI-generated content & risks

PartyDecider may use AI tools to generate suggestions, copy, or other outputs. AI outputs may be inaccurate, biased, or incomplete.

We do not guarantee truthfulness, safety, or suitability of AI-generated outputs. You use AI output at your own risk.

You are responsible for any decisions made using AI outputs (e.g., automated messages, suggested event text).

Do not feed personal sensitive data (health, financial, identity numbers) into AI features.

## 6. Payments, purchases & refunds

If you purchase premium features, subscriptions, or credits, payments are processed by third-party providers (e.g., Stripe). Your purchase is subject to their terms.

Price, tax, and billing info appear at checkout. You authorize us to charge the payment method you provide.

**Refunds:** Unless explicitly stated at purchase, paid transactions are non-refundable. For subscription cancellations, see the billing/FAQ page. We may amend refund policy for promotions or exceptional cases on a per-case basis.

## 7. Beta features & disclaimers

Some features may be in beta. Beta features are experimental and may change, fail, or be removed without notice. By using beta features you acknowledge added risk and limited support.

## 8. Privacy & data practices

Our Privacy Policy explains what personal data we collect, how we use it, retention, and user rights. By using the Service you consent to that collection and usage.

We collect analytics (aggregated, approximate location, device info) for product improvements. This data is anonymized and used in aggregate for dashboards and reporting.

## 9. Security & your responsibilities

We implement industry standard security controls, but we cannot guarantee absolute security. You must:

- Keep account credentials private.
- Report suspected breaches immediately.
- Not attempt to probe, penetrate, or attack our systems.

If you require admin-level access (e.g., owner email), we may require multi-factor authentication for that account.

## 10. Intellectual property

PartyDecider and FLW Connect Technologies own the Service, logos, designs, code, and trademarks unless otherwise stated.

You may not copy, reproduce, distribute, or create derivative works of our software or branding without written permission.

## 11. Third-party services & links

We may integrate third-party services (auth providers, payment processors, analytics). Those services are governed by their own terms. We are not responsible for their policies or availability.

Links to external sites are for convenience and do not imply endorsement.

## 12. Disclaimers & no warranties

THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE." WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.

We do not guarantee uptime, performance, accuracy of content, or that the Service meets your needs.

## 13. Limitation of liability

TO THE MAXIMUM EXTENT PERMITTED BY LAW, OUR AGGREGATE LIABILITY FOR ANY CLAIMS ARISING OUT OF THESE TERMS OR THE SERVICE IS LIMITED TO THE TOTAL AMOUNT YOU PAID US IN THE 12 MONTHS PRIOR TO THE CLAIM, OR ONE HUNDRED CANADIAN DOLLARS (CAD $100), WHICHEVER IS GREATER.

WE ARE NOT LIABLE FOR INDIRECT, SPECIAL, CONSEQUENTIAL, PUNITIVE, OR EXEMPLARY DAMAGES, LOSS OF PROFITS, LOSS OF DATA, OR LOSS OF BUSINESS.

## 14. Indemnification

You agree to indemnify and hold harmless PartyDecider, FLW Connect Technologies, and their officers, employees, agents, and affiliates from any claims, damages, losses, liabilities, and expenses (including legal fees) arising from your violation of these Terms or your User Content.

## 15. Termination & suspension

We may suspend or terminate accounts or access at our discretion, with or without notice, especially for security, legal compliance, or repeated violations.

Termination does not waive claims for breaches that occurred prior to termination.

## 16. Governing law & dispute resolution

These Terms are governed by the laws of British Columbia, Canada, excluding its conflicts-of-law rules.

For disputes, we prefer informal resolution first. If unresolved, parties submit to the exclusive jurisdiction of the courts of British Columbia.

## 17. Changes to Terms

We may modify these Terms. If changes are material, we will provide notice via email or in-app. Continued use after notice constitutes acceptance. Check this page periodically.

## 18. Additional safety & fraud provisions

You must not use PartyDecider to plan or facilitate illegal activity, solicit criminal services, or transmit contraband.

Automated scraping, harvesting of user data, or attempts to bypass rate limits or payments are prohibited.

Repeated abuse, creating multiple fake accounts, or coordinating to manipulate raffles/voting is strictly prohibited. We reserve the right to void suspicious raffles or votes and to freeze funds or credits pending investigation.

## 19. Refunds, chargebacks & fraud

You agree not to dispute legitimate charges without first contacting us. Excessive chargebacks or fraudulent chargebacks may result in account suspension and cooperation with payment processors and law enforcement.

## 20. Contact

For support, policy questions, or to report abuse: support@partydecider.com.
`;

export default function TermsModal({ onAccept, isSubmitting = false }) {
  const [hasRead, setHasRead] = useState(false);

  const handleAccept = () => {
    if (hasRead && !isSubmitting) {
      onAccept(CURRENT_TERMS_VERSION);
    }
  };

  return (
    <div className="terms-modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="terms-modal-title">
      <div className="terms-modal">
        <div className="terms-modal__header">
          <h2 id="terms-modal-title">Before you continue</h2>
          <p className="terms-modal__subtitle">Please review and accept our Terms & Conditions</p>
        </div>

        <div className="terms-modal__content" tabIndex={0}>
          <div className="terms-modal__text">
            {TERMS_CONTENT.split('\n').map((line, i) => {
              if (line.startsWith('# ')) {
                return <h1 key={i}>{line.slice(2)}</h1>;
              } else if (line.startsWith('## ')) {
                return <h2 key={i}>{line.slice(3)}</h2>;
              } else if (line.startsWith('**') && line.endsWith('**')) {
                return <p key={i}><strong>{line.slice(2, -2)}</strong></p>;
              } else if (line.startsWith('- ')) {
                return <li key={i}>{line.slice(2)}</li>;
              } else if (line.trim() === '') {
                return <br key={i} />;
              } else {
                return <p key={i}>{line}</p>;
              }
            })}
          </div>
        </div>

        <div className="terms-modal__footer">
          <label className="terms-modal__checkbox">
            <input
              type="checkbox"
              checked={hasRead}
              onChange={(e) => setHasRead(e.target.checked)}
              disabled={isSubmitting}
            />
            <span>I have read and agree to the Terms & Conditions</span>
          </label>

          <button
            className="terms-modal__button"
            onClick={handleAccept}
            disabled={!hasRead || isSubmitting}
          >
            {isSubmitting ? 'Processing...' : 'I Agree and Continue'}
          </button>
        </div>
      </div>
    </div>
  );
}

export { CURRENT_TERMS_VERSION };
